import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Search, Plus, Edit, Trash2, Eye, Loader2, AlertCircle, ScrollText, Check, MessageSquare, Mic, Webhook, PhoneForwarded, Sparkles, SearchCheck, Wand2, MessagesSquare, FlaskConical, ListChecks, PieChart, Play, ShieldCheck, AlertTriangle, Cpu } from 'lucide-react';
import {
  apiService,
  SystemGuideline,
  SystemGuidelinesType,
  CreateSystemGuidelineRequest,
  UpdateSystemGuidelineRequest,
  PromptGuidelineManifest,
  InternalAiLlmConfig
} from '../../services/api';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { GuidelinePreviewDialog } from './system-guidelines/GuidelinePreviewDialog';
import { toast } from 'sonner';

/**
 * Nicht-Prompt-Typen, die im Bereich „Systemrichtlinien" klassisch verwaltet
 * werden. Die KI-Template-Typen (assistant_*, prompt_*, conversation_analysis,
 * scenario_*) laufen ausschließlich über die KI-Werkzeug-Karten. Die früheren
 * Baustein-Typen wurden aufgelöst — ihre Inhalte sind in Wizard/Optimierer eingebettet.
 */
const SYSTEM_GUIDELINE_TYPES: SystemGuidelinesType[] = [
  'chat',
  'voice',
  'warm_forwarding_briefing',
  'hook_condition',
  'evaluation_classification',
];

/** Ein Prompt-Typ innerhalb einer KI-Werkzeug-Karte (mehrere bei Test-Anrufen). */
interface AiToolTypeEntry {
  type: SystemGuidelinesType;
  /** Zusatz-Beschriftung bei Karten mit mehreren Typen (sonst weggelassen). */
  entryLabel?: string;
  /** Optionale Kurzbeschreibung des Eintrags (bei Karten mit mehreren Einträgen). */
  entryDescription?: string;
}

/** Eine KI-Werkzeug-Karte in Alltagssprache (bündelt einen oder mehrere Prompt-Typen). */
interface AiToolCard {
  title: string;
  description: string;
  icon: typeof MessageSquare;
  entries: AiToolTypeEntry[];
}

/**
 * PM-freundliche Kurzansicht der KI-Werkzeuge. Jede Karte verweist auf die
 * aktive System-Richtlinie eines (oder mehrerer) Prompt-Typen; „Text bearbeiten"
 * öffnet den Klartext-Editor der aktiven Vorlage (kompletter roher Prompt).
 */
const AI_TOOL_CARDS: AiToolCard[] = [
  {
    title: 'Einrichtungsassistent',
    description: 'Führt Kunden per Dialog durch die Bot-Einrichtung.',
    icon: Sparkles,
    entries: [{ type: 'assistant_wizard' }],
  },
  {
    title: 'Prompt-Check',
    description: 'Prüft Bot-Konfigurationen auf Widersprüche und Lücken.',
    icon: SearchCheck,
    entries: [{ type: 'prompt_check' }],
  },
  {
    title: 'Prompt-Verbesserer',
    description: 'Erstellt aus gefundenen Problemen einen Verbesserungs-Vorschlag.',
    icon: Wand2,
    entries: [{ type: 'prompt_optimize' }],
  },
  {
    title: 'Gesprächs-Analyse',
    description: 'Vergleicht echte Gespräche mit der Bot-Konfiguration.',
    icon: MessagesSquare,
    entries: [{ type: 'conversation_analysis' }],
  },
  {
    title: 'Szenario-Test (Simulation)',
    description: 'Denkt kritische Szenarien durch, ohne echte Anrufe.',
    icon: FlaskConical,
    entries: [{ type: 'scenario_test' }],
  },
  {
    title: 'QA-Testfall-Vorschläge',
    description: 'Schlägt realistische Testfälle für das Teststudio vor.',
    icon: ListChecks,
    entries: [{ type: 'qa_scenario_suggest' }],
  },
];

/** Extrahiert eine lesbare Fehlermeldung aus einer API-Antwort (message kann String[] sein). */
function extractApiErrors(err: unknown): string[] {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    if (Array.isArray(message)) return message.map(String);
    if (typeof message === 'string') return [message];
  }
  if (err instanceof Error) return [err.message];
  return ['Unbekannter Fehler'];
}

export function SystemGuidelinesPage() {
  const navigate = useNavigate();

  // Data state
  const [guidelines, setGuidelines] = useState<SystemGuideline[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // KI-Werkzeuge: konfiguriertes Hintergrund-LLM für die Info-Leiste
  const [internalLlm, setInternalLlm] = useState<InternalAiLlmConfig | null>(null);

  // Filters
  const [selectedType, setSelectedType] = useState<SystemGuidelinesType | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewingGuideline, setViewingGuideline] = useState<SystemGuideline | null>(null);
  const [editingGuideline, setEditingGuideline] = useState<SystemGuideline | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isActivating, setIsActivating] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateSystemGuidelineRequest>({
    agentType: 'chat',
    name: '',
    description: '',
    content: ''
  });

  // Prompt-Template-Editor (CA-427): Manifest + Validierung
  const [manifest, setManifest] = useState<PromptGuidelineManifest>({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const manifestEntry = manifest[formData.agentType];

  // Load guidelines on mount and when filters change
  useEffect(() => {
    loadGuidelines();
  }, [includeInactive]);

  // Manifest einmalig laden (Palette, Sperren, Varianten-Tabs des Editors)
  useEffect(() => {
    apiService
      .getSystemGuidelineManifest()
      .then(setManifest)
      .catch(() => toast.error('Prompt-Template-Manifest konnte nicht geladen werden'));
  }, []);

  // Konfiguriertes Hintergrund-LLM der KI-Werkzeuge für die Info-Leiste laden.
  useEffect(() => {
    apiService
      .getInternalAiLlmConfig()
      .then(setInternalLlm)
      .catch(() => {
        /* Info-Leiste bleibt im Ladezustand — kein blockierender Fehler. */
      });
  }, []);

  const loadGuidelines = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.getSystemGuidelines(includeInactive);
      setGuidelines(data);
    } catch (err: any) {
      setError(err.message || 'System-Richtlinien konnten nicht geladen werden');
      toast.error('System-Richtlinien konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = (guideline: SystemGuideline) => {
    setViewingGuideline(guideline);
    setIsDialogOpen(true);
  };

  const resetEditorState = () => {
    setValidationErrors([]);
    setValidationWarnings([]);
  };

  const handleEdit = (guideline: SystemGuideline) => {
    setEditingGuideline(guideline);
    setFormData({
      agentType: guideline.agentType,
      name: guideline.name,
      description: guideline.description || '',
      content: guideline.content
    });
    resetEditorState();
    setViewingGuideline(null);
    setIsDialogOpen(true);
  };

  /** Öffnet die Prompt-Vorschau für die aktive Guideline eines KI-Werkzeugs (ohne Edit-Dialog). */
  const handlePreviewGuideline = (guideline: SystemGuideline) => {
    setFormData({
      agentType: guideline.agentType,
      name: guideline.name,
      description: guideline.description || '',
      content: guideline.content,
    });
    setIsPreviewOpen(true);
  };

  const handleCreate = () => {
    setEditingGuideline(null);
    setViewingGuideline(null);
    setFormData({
      agentType: selectedType !== 'all' ? selectedType : 'chat',
      name: '',
      description: '',
      content: ''
    });
    resetEditorState();
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Inhalt ist erforderlich');
      return;
    }

    try {
      setIsSaving(true);
      setValidationErrors([]);
      setValidationWarnings([]);

      // Prompt-Template-Typen vor dem Speichern gegen das Manifest validieren
      // (Live-Feedback; das Backend lehnt Verstöße ohnehin ab).
      if (manifestEntry) {
        const result = await apiService.validateSystemGuideline(formData.agentType, formData.content);
        setValidationWarnings(result.warnings);
        if (!result.valid) {
          setValidationErrors(result.errors);
          toast.error('Inhalt ist nicht gültig — bitte die gemeldeten Probleme beheben');
          return;
        }
      }

      if (editingGuideline) {
        const updateData: UpdateSystemGuidelineRequest = {
          name: formData.name,
          description: formData.description,
          content: formData.content
        };
        await apiService.updateSystemGuideline(editingGuideline.id, updateData);
        toast.success('Richtlinie aktualisiert');
      } else {
        await apiService.createSystemGuideline(formData);
        toast.success('Richtlinie erstellt');
      }

      closeDialog();
      await loadGuidelines();
    } catch (err: unknown) {
      const errors = extractApiErrors(err);
      setValidationErrors(errors);
      toast.error(errors[0] || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('System-Richtlinie wirklich loeschen?')) return;

    try {
      await apiService.deleteSystemGuideline(id);
      toast.success('Richtlinie geloescht');
      await loadGuidelines();
    } catch (err: any) {
      toast.error(err.message || 'Fehler beim Loeschen');
    }
  };

  const handleActivate = async (guideline: SystemGuideline) => {
    if (guideline.isActive) return;

    try {
      setIsActivating(guideline.id);
      await apiService.activateSystemGuideline(guideline.id);
      toast.success(`Richtlinie "${guideline.name}" aktiviert`);
      await loadGuidelines();
    } catch (err: any) {
      toast.error(err.message || 'Fehler beim Aktivieren');
    } finally {
      setIsActivating(null);
    }
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingGuideline(null);
    setViewingGuideline(null);
    setIsPreviewOpen(false);
    resetEditorState();
    setFormData({
      agentType: 'chat',
      name: '',
      description: '',
      content: ''
    });
  };

  // Bereich „Systemrichtlinien" zeigt nur die klassisch verwalteten Nicht-Prompt-Typen;
  // die KI-Template-Typen laufen über die Werkzeug-Karten.
  const filteredGuidelines = guidelines.filter(guideline => {
    const isSystemType = SYSTEM_GUIDELINE_TYPES.includes(guideline.agentType);
    const matchesType = selectedType === 'all' || guideline.agentType === selectedType;
    const matchesSearch = guideline.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (guideline.description?.toLowerCase().includes(searchTerm.toLowerCase()));
    return isSystemType && matchesType && matchesSearch;
  });

  const agentTypeLabels: Record<SystemGuidelinesType, string> = {
    chat: 'Chatbot',
    voice: 'Voicebot',
    warm_forwarding_briefing: 'Warm-Transfer',
    hook_condition: 'Hook-Bedingung',
    assistant_wizard: 'KI-Assistent (Dialog)',
    prompt_check: 'Prompt-Check',
    prompt_optimize: 'Prompt-Optimierer',
    conversation_analysis: 'Gesprächs-Analyse',
    scenario_test: 'Szenario-Test (Simulation)',
    qa_scenario_suggest: 'QA-Testfall-Vorschläge',
    evaluation_classification: 'Qualitative Auswertung',
  };

  const agentTypeIcons: Record<SystemGuidelinesType, typeof MessageSquare> = {
    chat: MessageSquare,
    voice: Mic,
    warm_forwarding_briefing: PhoneForwarded,
    hook_condition: Webhook,
    assistant_wizard: Sparkles,
    prompt_check: SearchCheck,
    prompt_optimize: Wand2,
    conversation_analysis: MessagesSquare,
    scenario_test: FlaskConical,
    qa_scenario_suggest: ListChecks,
    evaluation_classification: PieChart,
  };

  const getAgentTypeLabel = (type: SystemGuidelinesType): string => agentTypeLabels[type] ?? type;

  const getAgentTypeIcon = (type: SystemGuidelinesType) => agentTypeIcons[type] ?? ScrollText;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">System-Richtlinien</h1>
        <p className="text-gray-600">Globale Richtlinien fuer Chat- und Voicebots</p>
      </div>

      <Tabs defaultValue="ki-werkzeuge" className="space-y-4">
        <TabsList>
          <TabsTrigger value="ki-werkzeuge">KI-Werkzeuge</TabsTrigger>
          <TabsTrigger value="systemrichtlinien">Systemrichtlinien</TabsTrigger>
        </TabsList>

        {/* KI-Werkzeuge — PM-freundliche Kurzansicht der wichtigsten Prompt-Typen */}
        <TabsContent value="ki-werkzeuge" className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">KI-Werkzeuge</h2>
          <p className="text-sm text-gray-600">
            Passen Sie die inhaltliche Ausgestaltung der KI-Werkzeug-Prompts an. Technische Vorgaben
            (Formate, Verträge) bleiben fest und werden hier nicht angezeigt.
          </p>
        </div>

        {/* Info-Leiste: aktuell konfiguriertes Hintergrund-LLM der KI-Werkzeuge */}
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-blue-50/50 px-4 py-2 text-sm">
          <div className="flex items-center gap-2 text-gray-700">
            <Cpu className="h-4 w-4 text-blue-600" />
            <span>
              KI-Werkzeuge nutzen aktuell:{' '}
              {internalLlm ? (
                <span className="font-medium">
                  {internalLlm.provider} &middot; {internalLlm.model}
                </span>
              ) : (
                <span className="text-gray-400">wird geladen&hellip;</span>
              )}
            </span>
          </div>
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-blue-600"
            onClick={() => navigate('/background-job-llms')}
          >
            ändern unter Background-Job-LLMs
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {AI_TOOL_CARDS.map((card) => {
            const CardIcon = card.icon;
            return (
              <Card key={card.title} className="flex flex-col">
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-3">
                    <div className="rounded-md bg-blue-50 p-2">
                      <CardIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-base">{card.title}</CardTitle>
                      <CardDescription className="mt-1">{card.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="mt-auto space-y-3">
                  {card.entries.map((entry) => {
                    const active =
                      guidelines.find((g) => g.agentType === entry.type && g.isActive) ?? null;
                    return (
                      <div
                        key={entry.type}
                        className="space-y-2 border-t border-gray-100 pt-3 first:border-t-0 first:pt-0"
                      >
                        {entry.entryLabel && (
                          <div>
                            <div className="text-sm font-medium text-gray-700">{entry.entryLabel}</div>
                            {entry.entryDescription && (
                              <p className="text-xs text-gray-500">{entry.entryDescription}</p>
                            )}
                          </div>
                        )}
                        <div>
                          {active ? (
                            <Badge className="bg-green-100 text-green-800 text-xs">
                              <Check className="w-3 h-3 mr-1" />
                              Aktiv
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="border-amber-300 bg-amber-50 text-amber-700 text-xs"
                            >
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              keine aktive Vorlage — Werkzeug ist aus
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (active) handleEdit(active);
                            }}
                            disabled={!active}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Text bearbeiten
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (active) handlePreviewGuideline(active);
                            }}
                            disabled={!active}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Vorschau
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}
        </div>
        </TabsContent>

        {/* Systemrichtlinien — klassische Verwaltung der Nicht-Prompt-Typen */}
        <TabsContent value="systemrichtlinien" className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Systemrichtlinien</h2>
            <p className="text-sm text-gray-600">
              Grundlegende Bot-Richtlinien (Chatbot, Voicebot, Warm-Transfer, Hook-Bedingung) —
              Anlegen, Aktivieren, Versionieren und Löschen.
            </p>
          </div>
          <Button onClick={handleCreate} disabled={isLoading}>
            <Plus className="w-4 h-4 mr-2" />
            Neue Richtlinie
          </Button>
        </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="w-48">
          <Select
            value={selectedType}
            onValueChange={(value) => setSelectedType(value as SystemGuidelinesType | 'all')}
          >
            <SelectTrigger>
              <SelectValue placeholder="Typ auswaehlen..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Typen</SelectItem>
              {SYSTEM_GUIDELINE_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {agentTypeLabels[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            disabled={isLoading}
          />
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="includeInactive"
            checked={includeInactive}
            onCheckedChange={(checked) => setIncludeInactive(checked === true)}
          />
          <Label htmlFor="includeInactive" className="text-sm text-gray-600">
            Inaktive anzeigen
          </Label>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Lade System-Richtlinien...</span>
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={loadGuidelines}>Erneut versuchen</Button>
          </CardContent>
        </Card>
      ) : filteredGuidelines.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ScrollText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">
              {searchTerm || selectedType !== 'all'
                ? 'Keine Richtlinien gefunden'
                : 'Keine System-Richtlinien vorhanden'}
            </p>
            {!searchTerm && selectedType === 'all' && (
              <Button onClick={handleCreate}>
                Erste Richtlinie erstellen
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGuidelines.map((guideline) => {
            const TypeIcon = getAgentTypeIcon(guideline.agentType);
            return (
              <Card
                key={guideline.id}
                className={`hover:shadow-md transition-shadow ${!guideline.isActive ? 'opacity-60' : ''}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-xs">
                          <TypeIcon className="w-3 h-3 mr-1" />
                          {getAgentTypeLabel(guideline.agentType)}
                        </Badge>
                        {guideline.isActive ? (
                          <Badge className="bg-green-100 text-green-800 text-xs">
                            <Check className="w-3 h-3 mr-1" />
                            Aktiv
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">
                            Inaktiv
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="text-lg">{guideline.name}</CardTitle>
                      <CardDescription className="mt-1">
                        {guideline.description || 'Keine Beschreibung'}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 max-h-24 overflow-y-auto font-mono text-xs">
                      {guideline.content.substring(0, 200)}
                      {guideline.content.length > 200 && '...'}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500 space-y-1">
                        <div>Version: {guideline.version}</div>
                        <div>Erstellt: {new Date(guideline.createdAt).toLocaleDateString('de-DE')}</div>
                      </div>
                      <div className="flex items-center space-x-1">
                        {!guideline.isActive && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleActivate(guideline)}
                            disabled={isActivating === guideline.id}
                            title="Aktivieren"
                            className="text-green-600 hover:text-green-700"
                          >
                            {isActivating === guideline.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleView(guideline)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(guideline)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(guideline.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
        </TabsContent>
      </Tabs>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {viewingGuideline
                ? 'Richtlinie Details'
                : editingGuideline
                ? 'Richtlinie bearbeiten'
                : 'Neue Richtlinie'}
            </DialogTitle>
            <DialogDescription>
              {viewingGuideline
                ? 'Detaillierte Ansicht der System-Richtlinie'
                : 'Erstellen oder bearbeiten Sie eine System-Richtlinie fuer Bots'}
            </DialogDescription>
          </DialogHeader>

          {viewingGuideline ? (
            // View Mode
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Informationen</h4>
                    <div className="space-y-2 text-sm">
                      <div><strong>Name:</strong> {viewingGuideline.name}</div>
                      <div className="flex items-center gap-2">
                        <strong>Typ:</strong>
                        <Badge variant="outline">
                          {getAgentTypeLabel(viewingGuideline.agentType)}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <strong>Status:</strong>
                        {viewingGuideline.isActive ? (
                          <Badge className="bg-green-100 text-green-800">Aktiv</Badge>
                        ) : (
                          <Badge variant="secondary">Inaktiv</Badge>
                        )}
                      </div>
                      <div><strong>Version:</strong> {viewingGuideline.version}</div>
                      <div><strong>Erstellt:</strong> {new Date(viewingGuideline.createdAt).toLocaleDateString('de-DE')}</div>
                      <div><strong>Aktualisiert:</strong> {new Date(viewingGuideline.updatedAt).toLocaleDateString('de-DE')}</div>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Beschreibung</h4>
                  <p className="text-sm text-gray-600">
                    {viewingGuideline.description || 'Keine Beschreibung vorhanden'}
                  </p>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Inhalt</h4>
                <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
                    {viewingGuideline.content}
                  </pre>
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={closeDialog}>
                  Schliessen
                </Button>
                <Button onClick={() => handleEdit(viewingGuideline)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Bearbeiten
                </Button>
              </div>
            </div>
          ) : (
            // Edit/Create Mode
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="z.B. Chatbot Richtlinien v1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="agentType">Agent-Typ *</Label>
                  <Select
                    value={formData.agentType}
                    onValueChange={(value) => setFormData({ ...formData, agentType: value as SystemGuidelinesType })}
                    disabled={!!editingGuideline}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(agentTypeLabels) as SystemGuidelinesType[]).map((type) => (
                        <SelectItem key={type} value={type}>
                          {agentTypeLabels[type]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editingGuideline && (
                    <p className="text-xs text-gray-500">
                      Der Agent-Typ kann nicht geaendert werden
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Beschreibung</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optionale Beschreibung fuer die Verwaltung..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">Inhalt *</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder={manifestEntry ? 'Kompletter Prompt-Text…' : '<chatbot_richtlinien>&#10;  ...&#10;</chatbot_richtlinien>'}
                  className={`font-mono text-sm ${manifestEntry ? 'min-h-[500px]' : 'min-h-[400px]'}`}
                />
                {manifestEntry ? (
                  <div className="rounded-md border bg-gray-50 p-3 text-xs text-gray-600 space-y-2">
                    <p>
                      Der komplette Prompt als Klartext. <code>{'{{NAME}}'}</code>-Stellen füllt das System
                      zur Laufzeit mit Daten; <code>{'{{#IF …}}…{{/IF}}'}</code>-Blöcke gelten nur für die
                      jeweilige Variante (z.B. Voicebot/Chatbot). Beim Speichern wird geprüft, dass alle
                      Pflicht-Stellen erhalten sind.
                    </p>
                    {manifestEntry.placeholders.length > 0 && (
                      <div>
                        <p className="font-medium text-gray-700 mb-1">Platzhalter dieses Prompts:</p>
                        <ul className="space-y-0.5">
                          {manifestEntry.placeholders.map((placeholder) => (
                            <li key={placeholder.name}>
                              <code className="text-blue-700">{`{{${placeholder.name}}}`}</code>
                              {placeholder.required && <span className="ml-1 text-red-600 font-medium">(Pflicht)</span>}
                              {' — '}{placeholder.description}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {manifestEntry.variantGroups.length > 0 && (
                      <div>
                        <p className="font-medium text-gray-700 mb-1">Varianten-Blöcke:</p>
                        <ul className="space-y-0.5">
                          {manifestEntry.variantGroups.map((group) => (
                            <li key={group.key}>
                              <span className="text-gray-700">{group.label}:</span>{' '}
                              {group.values.map((value) => (
                                <code key={value.value} className="mr-1 text-blue-700">{`{{#IF ${group.key}=${value.value}}}`}</code>
                              ))}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">
                    {`Dieser Inhalt wird fuer alle ${getAgentTypeLabel(formData.agentType)}-Richtlinien verwendet. Verwenden Sie XML-Tags zur Strukturierung.`}
                  </p>
                )}
              </div>

              {validationErrors.length > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Inhalt ist nicht gültig</AlertTitle>
                  <AlertDescription>
                    <ul className="list-inside list-disc text-sm">
                      {validationErrors.map((message, idx) => (
                        <li key={idx}>{message}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {validationWarnings.length > 0 && (
                <Alert className="border-amber-300 bg-amber-50">
                  <ShieldCheck className="h-4 w-4 text-amber-600" />
                  <AlertTitle>Hinweise</AlertTitle>
                  <AlertDescription>
                    <ul className="list-inside list-disc text-sm">
                      {validationWarnings.map((message, idx) => (
                        <li key={idx}>{message}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end space-x-2 pt-4">
                {manifestEntry && (
                  <Button
                    variant="outline"
                    onClick={() => setIsPreviewOpen(true)}
                    disabled={isSaving || !formData.content.trim()}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Vorschau
                  </Button>
                )}
                <Button variant="outline" onClick={closeDialog} disabled={isSaving}>
                  Abbrechen
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Speichern...
                    </>
                  ) : (
                    editingGuideline ? 'Speichern' : 'Erstellen'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {manifestEntry && (
        <GuidelinePreviewDialog
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          agentType={formData.agentType}
          content={formData.content}
          manifest={manifestEntry}
        />
      )}
    </div>
  );
}
